import { Component, OnInit, Inject, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { CoreLibModalComponent } from 'app/core-lib/dialogs/modal/modal.component';
import { DialogRef } from '../modal/dialog-ref';
import { DIALOG_DATA } from '../modal/dialog-tokens';
import { CasesService } from 'app/shared/cases/services/cases.service';
import { ActivitiesService } from 'app/shared/cases';
import { forkJoin, Subscription, timer } from 'rxjs';
import { concatMap, finalize, map, take, takeWhile, tap } from 'rxjs/operators';
import { LogType } from 'app/shared/logs/types';
import { ModalService } from '../modal/modal.service';
import { CoreLibShareModalComponent } from '../share-modal/share-modal.component';
import { UtilService } from 'app/shared/util.service';
import { AlertService } from 'app/core-lib/services/alerts/alert.service';
import { FilesService } from 'app/shared/services/files.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CoreLibEmptyModalComponent } from '../empty-modal/empty-modal.component';
import { Store } from '@ngxs/store';
import { DefaultLanguageState } from 'app/states/default-language/default-language.state';
import { Activity } from 'app/new-fs/desk/models/Activity.model';
import * as FileSaver from 'file-saver';
import { BreakpointService } from 'app/core-lib/services/breakpoints/breakpoint.service';
import { animate, style, transition, trigger } from '@angular/animations';
import { DateTime } from 'luxon';
import { Case } from 'app/shared/model/guest-answer.model';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { UsersState } from 'app/states/users/users.state';
import { FidelityDeskState } from 'app/states/fidelity-desk/fidelity-desk.state';
import { User } from 'app/shared/model';
import { UsersActionsState } from 'app/states/users-actions/users-actions.state';
import { TeamsService } from 'app/new-fs/gestor-usuarios/teams/teams.service';
import { Team } from 'app/new-fs/gestor-usuarios/teams/model/teams.model';

const LOG_ICONS = {
  caseChangeState: 'bell',
  caseChangeAssigned: 'user-edit',
  editCase: 'pencil',
  createCase: 'plus',
  default: 'pencil',
};
const FOLLOWUP_SURVEY = '1_1';
const ONSITE_SURVEY = '2_1';
const ONLINE_REVIEW = '3_2';
const PRESTAY_PRECHECKIN = '4_3';
const PRESTAY_ORDER = '4_4';
const ACTIVITY_COMMENT_TYPE = { id: 1 };
const COMMENT_SYNC_MAX_ATTEMPTS = 4;
const COMMENT_SYNC_INTERVAL_MS = 600;
const CREATE_COMMENT_METHOD = 'createComment';
const TEAMS_PAGE_SIZE = 50;

enum StatesTypes {
  PENDING = 1,
  PROCESS = 2,
  FINISHED = 3,
  DISCARDED = 4,
}

/**
 * Motivos por los que un caso queda bloqueado para escritura, en orden de precedencia.
 * Los motivos de equipo van primero: el equipo de un caso ya creado no se puede cambiar,
 * así que si el equipo no sirve no hay ninguna reasignación posible que ofrecer.
 */
export enum CaseBlockReason {
  NONE = 'none',
  CHECKING = 'checking',
  TEAM_DELETED = 'team_deleted',
  TEAM_WITHOUT_MEMBERS = 'team_without_members',
  ASSIGNED_DELETED = 'assigned_deleted',
  ASSIGNED_OUT_OF_TEAM = 'assigned_out_of_team',
}

const ASSIGNED_BLOCK_REASONS = [CaseBlockReason.ASSIGNED_DELETED, CaseBlockReason.ASSIGNED_OUT_OF_TEAM];
const TEAM_BLOCK_REASONS = [CaseBlockReason.TEAM_DELETED, CaseBlockReason.TEAM_WITHOUT_MEMBERS];

@Component({
    selector: 'mh-core-lib-case-detail-modal',
    templateUrl: './case-detail-modal.component.html',
    styleUrls: ['./case-detail-modal.component.scss'],
    animations: [
        trigger('fadeSlideInOut', [
            transition(':enter', [
                style({ transform: 'translateY(100%)' }),
                animate('500ms', style({ transform: 'translateY(0)' })),
            ]),
            transition(':leave', [animate('500ms', style({ transform: 'translateY(100%)' }))]),
        ]),
    ],
    standalone: false
})
export class CoreLibCaseDetailModalComponent extends CoreLibModalComponent implements OnInit, OnDestroy {
  base;
  languageId = this.store.selectSnapshot(DefaultLanguageState.languageId);
  stateTypesEnum: typeof StatesTypes = StatesTypes;
  editing = false;
  descriptionForm;
  commentForm: FormGroup;
  mentionState;
  caseData;
  uploadingFiles = false;
  completedActions = false;
  loadingImage = false;
  showCaseForm = false;
  showCommentForm = false;
  selectedFile: File;
  activities = [];
  comments = [];
  allResources = [];
  downloadedFiles = [];
  waitingFileDownload = [];
  newActivities: Activity[] = [];
  loadingActivities = false;
  acceptedImageTypes: string[] = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif', 'image/svg+xml', 'image/webp'];
  acceptedDocumentTypes: string[] = ['pdf', 'docx', 'doc', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'heic'];
  acceptedExtensionImageFiles: string[] = ['jpeg', 'png', 'jpg', 'gif', 'svg', 'webp'];
  videoExtensionsToDownload: string[] = ['mp4', 'webm', 'ogg', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'mpeg', '3gp'];
  readonly TRANSLATE_KEY = 'cases.detail_modal.'
  imageLink;
  fileInput: HTMLInputElement;
  filesUploaded: File[] = [];
  dueDateText: string;
  dueTimeText: string;
  defaultProfilePhoto = 'https://statics.myhotel.io/uploads/fidelity-suite/users/default_user.png';
  showDiscardCase = false;
  sendingComment = false;
  isSubscriber = false;
  readonly INVALID_SLUG = 'INVALID_SLUG'
  readonly userRole = this.store.selectSnapshot(UsersActionsState.role);
  readonly BLOCK_KEY = 'cases.detail_modal.blocked.';

  /** null = no se pudo determinar (no cargado aún, equipo no encontrado, o error de red) */
  teamData: Team = null;
  usersFromTeam = [];
  checkingTeam = false;

  // Estado derivado del caso. Son campos y no getters a propósito: todos están bindeados en el
  // template, y como getters `blockReason` terminaba evaluándose una decena de veces por cada
  // ciclo de detección de cambios. Se recalculan en refreshCaseState(), desde los pocos puntos
  // donde cambian sus entradas.
  blockReason: CaseBlockReason = CaseBlockReason.NONE;
  isBlocked = false;
  isTeamBlockReason = false;
  showBlockedBanner = false;
  blockedBannerKey = '';
  statusMessage = 'core-lib.action-wrapper.block';
  messageParams: Record<string, any> = {};
  assignedOptions = [];
  users = [];
  canEditProperties = false;
  canEditTitleAndDescription = false;
  canEditAssigned = false;

  PRODUCT_MODULE_NAMES = {
    [FOLLOWUP_SURVEY]: 'Follow Up',
    [ONSITE_SURVEY]: 'Onsite',
    [ONLINE_REVIEW]: 'Online',
    [PRESTAY_PRECHECKIN]: 'Prestay Web Check in',
    [PRESTAY_ORDER]: 'Prestay Servicios',
  };

  subscriptions = new Subscription();
  waiting = true;
  loading = false;
  waitingRecords = true;
  isExpired = false;
  remainingPercentage = 0;
  optionStates = [
    { id: 1, value: 'pending', selected: false },
    { id: 2, value: 'process', selected: false },
    { id: 3, value: 'finished', selected: false },
    { id: 4, value: 'discarded', selected: false },
  ];
  colorVariants = {
    blue: 'tw-bg-sky-500',
    yellow: 'tw-bg-yellow-500',
    red: 'tw-bg-red-500',
  };

  constructor(
    protected dialogRef: DialogRef,
    @Inject(DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private casesService: CasesService,
    private activitiesService: ActivitiesService,
    private modalService: ModalService,
    private utilService: UtilService,
    private alertService: AlertService,
    private filesService: FilesService,
    private sanitizer: DomSanitizer,
    private store: Store,
    private breakpointService: BreakpointService,
    private translateService: TranslateService,
    private router: Router,
    private teamsService: TeamsService,
  ) {
    super(dialogRef, data);
  }

  ngOnInit(): void {
    this.setCase();
    this.setAssignedUsers();
    this.setDescriptionForm();
    this.setCommentForm();
    this.fetchActivitiesComments();
    this.getFormatDueDate();
    this.subscriptions.add(
      this.updatedCase$.subscribe(({ casePublic, subscribers }) => {
        this.markStagnantStatus(casePublic);
        this.fetchActivitiesComments();
        this.data = {
          ...this.data,
          subscribers,
        };

        this.setCase({ ...casePublic, product_item: this.data.case.product_item });
      }),
    );

    this.subscriptions.add(
      this.isDataForModalReady$.subscribe((response) => {
        this.isSubscriber = !!response.subscribers?.find( suscriber => suscriber.id === this.currentUser.id );
        this.waiting = false;

        this.data = {
          ...this.data,
          ...response,
        };
        this.refreshCaseState(); // isSubscriber alimenta statusMessage
      }),
    );
    this.base = this.store.selectSnapshot(FidelityDeskState.base);
  }

  getFormatDueDate() {
    if (this.caseData?.due_date) {
      const dueDate = this.caseData.due_date;
      this.dueDateText = DateTime.fromISO(dueDate.split(' ')[0]).toFormat('dd/LL/yyyy');
      this.dueTimeText = dueDate.split(' ')[1];
    }
  }

  async checkAssigned() {
    try {
      const res = await this.casesService.seenByAssignedUser(this.caseData.id).toPromise();
      if (res && this.caseData?.assigned?.id === this.currentUser.id) {
        const msg = this.translateService.instant('cases.detail_modal.reviewed_now', {
          firstName: this.caseData?.assigned?.first_name,
          lastName: this.caseData?.assigned?.last_name,
        });
        this.caseData = { ...this.caseData, viewed: true, viewed_text: msg };
      }
    } catch (error) {
      console.error(error);
    }
  }

  markStagnantStatus(caseValueEntry) {
    this.caseData.stagnant = caseValueEntry.stagnant;
  }

  /**
   * Trae el equipo del caso y deja en `usersFromTeam` solo los miembros que pueden ser
   * responsables. Solo aplica a casos de equipo.
   */
  async setAssignedUsers() {
    if (!this.caseData?.team_details) return;
    // El endpoint no devuelve equipos eliminados: pedirlo es una request garantizada a vacío.
    // El bloqueo lo resuelve resolveBlockReason() leyendo team_details.deleted.
    if (this.caseData.team_details.deleted) return;
    this.checkingTeam = true;
    this.refreshCaseState(); // entra en CHECKING: bloquea mientras no se sabe si el responsable vale
    try {
      // page_size explícito: el default del servicio es 10 y hay clientes con más equipos,
      // en cuyo caso el find() no encuentra el equipo y el selector queda vacío.
      const res = await this.teamsService
        .getTeamsByCustomerId(this.customerId, 0, TEAMS_PAGE_SIZE)
        .toPromise();
      this.teamData = res?.content?.find((team) => team.id === this.caseData.team_details.id) ?? null;

      // Hay que excluir dos cosas distintas y el endpoint de equipos solo conoce la primera:
      //   - sacado de ESTE equipo        -> `user.deleted`
      //   - desactivado de la plataforma -> el equipo no lo sabe
      // Por eso se cruza contra `users`, que ya viene sin los `flag_deleted`: estar en esa
      // lista es la prueba de que el usuario sigue habilitado.
      // OJO: los miembros de equipo usan `user_id`; los de forcases usan `id`.
      // TODO: si el backend agrega `flag_deleted` a los miembros de equipo, esto se reduce a
      //       .filter((user) => !user.deleted && !user.flag_deleted) y se borra el Set.
      const activeUserIds = new Set((this.users ?? []).map((user) => user.id));
      this.usersFromTeam = (this.teamData?.users ?? []).filter(
        (user) => !user.deleted && activeUserIds.has(user.user_id),
      );
    } catch (error) {
      console.error(error);
      this.teamData = null;
    } finally {
      this.checkingTeam = false;
      this.refreshCaseState(); // sale de CHECKING: ya se conoce el equipo (o que no se pudo saber)
    }
  }

  /**
   * Recalcula todo el estado derivado del caso. Se llama desde los pocos puntos donde cambian
   * sus entradas —`setCase()`, las dos transiciones de `setAssignedUsers()` y la suscripción
   * que setea `isSubscriber`— y NUNCA desde el template.
   */
  private refreshCaseState() {
    // `flag_deleted` = desactivado de la plataforma. Nombre distinto al `deleted` de Assigned
    // y al de los miembros de equipo, que significa otra cosa (sacado de ese equipo).
    // Filtrar acá cubre también la lista de seguidores, que deriva de esta.
    this.users = (this.store.selectSnapshot(UsersState.usersForCases) || []).filter(
      (user) => !user.flag_deleted,
    );

    this.blockReason = this.resolveBlockReason();
    this.isBlocked = this.blockReason !== CaseBlockReason.NONE;
    // Motivos que solo se arreglan en la configuración de equipos, fuera de este modal.
    this.isTeamBlockReason = TEAM_BLOCK_REASONS.includes(this.blockReason);
    // El banner espera a que el equipo resuelva, para no parpadear al abrir el modal.
    this.showBlockedBanner = this.isBlocked && this.blockReason !== CaseBlockReason.CHECKING;

    const canFix = this.canUnblock && ASSIGNED_BLOCK_REASONS.includes(this.blockReason);
    this.blockedBannerKey = `${this.BLOCK_KEY}${this.blockReason}.${canFix ? 'banner' : 'banner_readonly'}`;

    // Un solo objeto para las dos superficies —tooltip y banner— para que no se desincronicen.
    // Incluye `role` porque los mensajes no bloqueantes lo interpolan.
    this.messageParams = {
      role: this.userRole,
      team: this.caseData?.team_details?.name,
      firstName: this.caseData?.assigned?.first_name,
      lastName: this.caseData?.assigned?.last_name,
    };

    this.statusMessage = this.resolveStatusMessage();
    this.assignedOptions = this.isOfTeam ? this.usersFromTeam : this.users;
    this.canEditProperties = !this.isBlocked && (this.isSA || this.isCreator || this.isAssigned);
    this.canEditTitleAndDescription = !this.isBlocked && (this.isSA || this.isCreator);
    this.canEditAssigned = this.resolveCanEditAssigned();
  }

  /**
   * Devuelve UN motivo de bloqueo, en orden de precedencia. Los motivos de equipo van antes
   * que los de responsable porque, si el equipo está eliminado o vacío, no existe ninguna
   * reasignación posible: pedirle al usuario que elija otro responsable sería mentirle.
   */
  private resolveBlockReason(): CaseBlockReason {
    // Va primero y no depende de `teamData`: el equipo eliminado se sabe desde el propio caso.
    // `teams/customer/{id}` NO devuelve equipos eliminados, así que buscarlo ahí siempre da
    // "no encontrado" y este motivo nunca se dispararía.
    if (this.isOfTeam && this.caseData?.team_details?.deleted) return CaseBlockReason.TEAM_DELETED;

    if (this.checkingTeam) return CaseBlockReason.CHECKING;

    // teamData en null significa "no se pudo determinar" (fetch fallido o equipo fuera de la
    // página): solo se saltean los motivos que necesitan la lista de miembros.
    if (this.isOfTeam && this.teamData && this.usersFromTeam.length === 0) {
      return CaseBlockReason.TEAM_WITHOUT_MEMBERS;
    }

    if (this.caseData?.assigned?.deleted) return CaseBlockReason.ASSIGNED_DELETED;

    if (
      this.isOfTeam &&
      this.teamData &&
      !this.usersFromTeam.some((user) => user.user_id === this.caseData?.assigned?.id)
    ) {
      return CaseBlockReason.ASSIGNED_OUT_OF_TEAM;
    }

    return CaseBlockReason.NONE;
  }

  private resolveStatusMessage() {
    if (this.isBlocked && this.blockReason !== CaseBlockReason.CHECKING) {
      return `${this.BLOCK_KEY}${this.blockReason}.tooltip`;
    }
    return this.isAssigned ? ((this.TRANSLATE_KEY + 'responsable_message')) :
    this.isSubscriber ? ((this.TRANSLATE_KEY + 'suscriber_message')) :
    'core-lib.action-wrapper.block'
  }

  /**
   * El selector de responsable es la única escritura que sobrevive al bloqueo… salvo con un
   * motivo de equipo: el backend no permite cambiar el equipo de un caso ya creado, así que
   * no hay ninguna opción válida que ofrecer. Un desplegable habilitado y vacío sugiere que
   * sí la hay; mejor deshabilitarlo y dejar que el banner explique qué hacer. Mismo criterio
   * mientras el equipo carga: todavía no hay opciones.
   */
  private resolveCanEditAssigned() {
    if (!this.isBlocked) return this.canEditProperties;
    if (this.isTeamBlockReason || this.blockReason === CaseBlockReason.CHECKING) return false;
    return this.canUnblock;
  }

  get canUnblock() {
    return this.isSA || this.isCreator;
  }

  fetchActivitiesComments() {
    this.loadingActivities = true;
    const languageId = this.languageId || this.utilService.getCurrentLanguageId();
    this.subscriptions.add(
      this.activitiesService.getLogsForCase(this.caseData.id, languageId).subscribe(
        (activities: Activity[]) => {
          this.applyActivities(activities);
          this.loadingActivities = false;
        },
        () => (this.loadingActivities = false),
      ),
    );
  }

  private mapActivities(activities: Activity[]): Activity[] {
    return (activities || []).map((activity) => ({
      ...activity,
      activity_text: activity.activity_text ? activity.activity_text.replace(/\n/g, '<br>') : '',
      log_content: {
        ...activity.log_content,
        text: activity?.log_content?.text ? activity.log_content.text.replace(/\n/g, '<br>') : '',
      },
    }));
  }

  private applyActivities(activities: Activity[]): void {
    if (activities?.length > 0) {
      this.newActivities = this.mapActivities(activities);
    }
  }

  private idOf(activity: Activity): number | string {
    return activity?.sub_resource_id ?? activity?.resource_id ?? activity?.log_content?.id;
  }

  private getCommentIds(activities: Activity[]): Set<number | string> {
    return new Set(
      (activities || [])
        .filter((activity) => activity?.request_method_name === CREATE_COMMENT_METHOD)
        .map((activity) => this.idOf(activity))
        .filter((id) => id !== undefined && id !== null),
    );
  }

  private hasNewComment(
    activities: Activity[],
    newId: number | string | undefined,
    existingIds: Set<number | string>,
  ): boolean {
    const commentActivities = (activities || []).filter(
      (activity) => activity?.request_method_name === CREATE_COMMENT_METHOD,
    );
    if (newId !== undefined && newId !== null) {
      if (commentActivities.some((activity) => this.idOf(activity) === newId)) {
        return true;
      }
    }
    return commentActivities.some((activity) => !existingIds.has(this.idOf(activity)));
  }

  private pollForNewComment(newId: number | string | undefined, existingIds: Set<number | string>): void {
    this.loadingActivities = true;
    const languageId = this.languageId || this.utilService.getCurrentLanguageId();

    this.subscriptions.add(
      timer(0, COMMENT_SYNC_INTERVAL_MS)
        .pipe(
          take(COMMENT_SYNC_MAX_ATTEMPTS),
          concatMap(() => this.activitiesService.getLogsForCase(this.caseData.id, languageId)),
          map((activities: Activity[]) => activities || []),
          tap((activities: Activity[]) => this.applyActivities(activities)),
          takeWhile((activities: Activity[]) => !this.hasNewComment(activities, newId, existingIds), true),
        )
        .subscribe({
          complete: () => (this.loadingActivities = false),
          error: () => (this.loadingActivities = false),
        }),
    );
  }

  setActivities(logs) {
    const NON_SHOWN_CHANGES = ['id', 'case_id', 'typeid', 'typename', 'item_id', 'product_id'];

    return (logs || [])
      .filter((log) => log.sub_resource_name !== 'action' && log.modifications && log.modifications.length > 0)
      .map((log) => {
        const operation = log.request_method === 'POST' ? 'ADD' : 'EDIT'; //podria corregirlo el back
        const productType = (log.modifications.find((l: any) => l.name === 'product_id') || {}).actual;
        const changes = (log.modifications || [])
          .filter((c: any) => !NON_SHOWN_CHANGES.includes(c.name))
          .map((c: any) => ({ ...c, operation }))
          .map((c: any) => (c.name !== 'createdAt' ? c : { ...c, actual: `${c.actual} UTC` }))
          .map((c: any) =>
            c.name !== 'product_item_type'
              ? c
              : {
                  ...c,
                  actual: {
                    ...c.actual,
                    name: this.PRODUCT_MODULE_NAMES[`${productType}_${c.actual.id}`],
                  },
                },
          );

        return {
          ...log,
          changes,
          resourceName: log.resource_name,
          type: LogType[log.request_method as keyof LogType],
          icon: 'fa-' + (LOG_ICONS[log.request_method_name] || LOG_ICONS.default),
          operation,
        };
      })
      .sort((l1, l2) => (l1.timestamp > l2.timestamp ? -1 : 1));
  }

  setCase(updatedCase: Case = null) {
    const caseData = updatedCase || this.data.case;
    this.caseData = { ...caseData };
    // Único punto que cambia assigned / team_details / is_of_team, así que es donde hay que
    // recalcular el estado de bloqueo. Cubre la carga inicial y cada refetch tras reasignar.
    this.refreshCaseState();
    if (!updatedCase) this.getAttachments();
    this.getPercentages();
    this.checkAssigned();
  }

  setDescriptionForm() {
    this.descriptionForm = this.fb.group({
      title: this.caseData.title,
      description: this.caseData.description,
    });
  }

  setCommentForm() {
    this.commentForm = this.fb.group({
      comment: [''],
    });
  }

  handleEditing() {
    this.setDescriptionForm(); // reset description value
    this.editing = true;
  }

  async handleAcceptEditDescription() {
    this.replaceMentionsBeforeSubmit(this.descriptionForm, 'description');
    this.loading = true;
    const obs$ = [];
    if (this.caseData.title !== this.descriptionForm.value.title) {
      obs$.push(this.casesService.updateTitle(this.caseData.id, this.descriptionForm.value.title));
    }

    if (this.caseData.description !== this.descriptionForm.value.description) {
      obs$.push(this.casesService.updateDescription(this.caseData.id, this.descriptionForm.value.description));
    }

    this.caseData.title = this.descriptionForm.value.title;
    this.caseData.description = this.descriptionForm.value.description;

    try {
      const res = await forkJoin(obs$).toPromise();
      if (res) {
        this.emitCaseFn(this.caseData);
        this.utilService.ga('cases', 'desk-click-edit-case');
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.editing = false;
      this.loading = false;
    }
  }

  handleCancelEditDescription() {
    this.editing = false;
  }

  handleUpdatedCase($event) {
    this.emitCaseFn($event);
  }

  handleRecordCaseDetailAction() {
    this.waitingRecords = true;
    this.emitCaseFn(this.caseData);
  }

  async handleStateChange(option) {
    if (this.caseData.state.id === option.id) return;
    this.updateCaseState(option);
  }

  async updateCaseState(option) {
    this.loading = true;
    try {
      const res = await this.casesService.updateState(this.caseData.id, option.id).toPromise();
      if (res) {
        this.caseData.state = { ...this.caseData.state, id: option.id };
        this.emitCaseFn(this.caseData);
        this.showNotification('cases.commons.success_state');
        this.handleUpdatedCase(this.caseData);
        this.utilService.ga('cases', 'desk-click-change-stage');
        this.loading = false;
      }
    } catch (error) {
      console.error(error);
      this.showNotification('cases.commons.error_state', 'error');
      this.loading = false;
    }
  }

  showNotification(messageKey: string, type = 'success') {
    this.alertService.handleAlert(messageKey, 5000, 'bottom', 'end', `snackbar-panel-${type}-user-config`);
  }

  handleShareCase() {
    const classes = ['overlay-panel', 'fit-height-content'];
    const backdropClasses = ['overlay-backdrop-inherit'];
    const config = { data: { case: this.caseData } };
    this.modalService.open(CoreLibShareModalComponent, config, classes, backdropClasses);
    this.utilService.ga('cases', 'desk-click-share-modal');
  }

  handleMentionState(mention) {
    this.mentionState = mention;
  }

  submitComment() {
    this.sendingComment = true;
    this.replaceMentionsBeforeSubmit(this.commentForm, 'comment');
    const payload = {
      author_id: this.currentUser.id,
      case_id: this.caseData.id,
      text: this.commentForm.get('comment')?.value || '',
      type: ACTIVITY_COMMENT_TYPE,
      attachments: this.filesUploaded || [],
    };

    const existingCommentIds = this.getCommentIds(this.newActivities);

    this.subscriptions.add(
      this.activitiesService
        .createComment(payload)
        .pipe(finalize(() => (this.sendingComment = false)))
        .subscribe({
          next: (res) => {
            if (!res) return;
            this.commentForm.get('comment').reset();
            this.showNotification('cases.detail_modal.added_comment');
            this.pollForNewComment(res.id, existingCommentIds);
            this.completedActions = true;
            this.filesUploaded = [];
            if (this.isMobileOrTablet) this.showCommentForm = false;
            this.utilService.ga('cases', 'desk-click-comment-case');
          },
          error: (error) => console.error(error),
        }),
    );
  }

  replaceMentionsBeforeSubmit(form: FormGroup, controlKey: string) {
    const control = form.get(controlKey);
    const value = control.value;
    const matches = value?.match(/@\[(.*?)\]/g) || [];
    matches?.forEach((match) => {
      const mention = this.mentionState?.find((findedMention) => findedMention.key === match);
      if (mention) {
        const text = control.value.replace(match, mention.value);
        control.setValue(text);
      }
    });
  }

  getAttachments() {
    if (this.caseData.attachments.length === 0) return;
    this.caseData.attachments.forEach((file: File) => {
      const fileExtension = file.file_key.split('.')[1].toLowerCase();
      if (this.acceptedExtensionImageFiles.includes(fileExtension)) {
        this.handleDowloadEvent(file);
      } else {
        file = { ...file, type: `application/${fileExtension}` };
        this.downloadedFiles.push(file);
      }
    });
  }

  async handleDowloadEvent(file: File, source = 'description', needToDownload = false) {
    const fileExtension = file.file_key.split('.')[1].toLowerCase();
    const type = this.acceptedDocumentTypes.includes(fileExtension)
      ? `application/${fileExtension}`
      : `image/${fileExtension}`;

    try {
      const res = await this.filesService.downloadFileToShow(this.customerId, file.file_key).toPromise();
      if (res) {
        this.downloadFile(res, type, source, file, needToDownload);
      }
    } catch (error) {
      console.error(error);
    }
  }

  downloadFile(res, type: string, source: string, initialFileData?: File, needToDownload = false) {
    const file = new Blob([res], { type });
    const blob = window.URL.createObjectURL(file);
    const sanitizeLink = this.sanitizer.bypassSecurityTrustResourceUrl(blob);
    const extensionType = type.split('/')[1];

    if (
      this.acceptedDocumentTypes.includes(extensionType) ||
      this.videoExtensionsToDownload.includes(extensionType) ||
      needToDownload
    ) {
      FileSaver.saveAs(file, this.selectedFile?.file_name || initialFileData?.file_name);
      return;
    }

    if (source === 'description') {
      const fileData = {
        link: sanitizeLink,
        type,
        initialFileData,
      };
      this.downloadedFiles.push(fileData);
    } else {
      this.expandImage(sanitizeLink, initialFileData);
    }
  }

  getPercentages() {
    if (this.caseData?.time_management?.remaining_time < 0) {
      this.isExpired = true;
      return;
    }
    const total = this.caseData?.time_management?.total_time;
    const remaining = this.caseData?.time_management?.remaining_time;
    this.remainingPercentage = Math.round((remaining * 100) / total);
  }

  expandImage(file: SafeResourceUrl, fileData?: File) {
    this.selectedFile = fileData;
    const config = {
      data: {
        file,
        type: 'image',
      },
    };
    const backdropClasses = ['overlay-backdrop-inherit'];
    const modalRef = this.modalService.open(CoreLibEmptyModalComponent, config, null, backdropClasses);
    modalRef.afterClosed().subscribe(async (response) => {
      if (response && response === 'download') {
        this.loadingImage = true;
        await this.handleDowloadEvent(fileData, 'description', true);
        this.loadingImage = false;
      }
    });
  }

  handleFilesUploaded(files: File[]) {
    this.filesUploaded = [...this.filesUploaded, ...files];
    this.utilService.ga('cases', 'desk-click-attach-documents');
  }

  removeUploadedFile(fileToRemove: { file_key: string; file_name: string }) {
    this.filesUploaded = this.filesUploaded.filter(file => file.file_key !== fileToRemove.file_key);
  }

  async showOrDownloadFile(file: File, source: string) {
    this.selectedFile = file;
    try {
      this.loadingImage = true;
      await this.handleDowloadEvent(file, source);
      this.loadingImage = false;
    } catch (error) {
      console.error(error);
    }
  }

  async handleDiscardCase($event) {
    const { reason, reasonComment } = $event;
    const body = {
      case_id: this.caseData.id,
      reason_id: reason,
      reason_comment: reasonComment ?? null,
    };
    try {
      const resUpdate = await this.casesService.discardCase(body).toPromise();
      if (resUpdate) {
        this.caseData.state = { ...this.caseData.state, id: this.stateTypesEnum.DISCARDED };
        this.emitCaseFn(this.caseData);
        this.showNotification('cases.commons.success_state');
        this.handleUpdatedCase(this.caseData);
        this.utilService.ga('cases', 'desk-click-change-stage');
      }
    } catch (error) {
      console.error(error);
      this.showNotification('cases.commons.error_state', 'error');
    }
  }

  // Los siguientes se quedan como getters: solo se llaman desde TypeScript (refreshCaseState),
  // nunca desde el template, así que se evalúan una vez por recálculo y no por ciclo.
  get isOfTeam() {
    return !!this.caseData?.is_of_team;
  }

  get isSA() {
    return this.currentUser.superAdmin || (this.currentUser?.user_role?.toLowerCase() === "administrator");
  }

  get isCreator() {
    return this.caseData.author.id === this.currentUser.id
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  get title() {
    return this.caseData.title;
  }

  get created() {
    return this.caseData.created_at;
  }

  get createdBy() {
    return `${this.caseData.author.first_name} ${this.caseData.author.last_name}`;
  }

  get publicId() {
    return this.caseData.public_id;
  }

  get description() {
    return this.caseData.description;
  }

  get isReview() {
    return this.caseData.product_item?.type.name === 'review';
  }

  get isNps() {
    return this.caseData.product_item?.type.name === 'survey-answer';
  }

  get isPrestayPrecheckin() {
    return this.caseData.product_item?.type.name === 'pre-checkin';
  }

  get isPrestayOrder() {
    return this.caseData.product_item?.type.name === 'order';
  }

  get customerId() {
    return this.data.currentCustomerId;
  }

  get types() {
    return this.data.types;
  }

  get displayFnTypes() {
    return this.data.translatesPipe.types;
  }

  get priorities() {
    return this.data.priorities;
  }

  get displayFnPriorities() {
    return this.data.translatesPipe.priorities;
  }

  get areas() {
    return this.data.areas;
  }

  get displayChipsFn() {
    return this.data.translatesPipe.chips;
  }

  get states() {
    return this.data.states;
  }

  get subscribers() {
    return this.data.subscribers;
  }

  get currentUser() {
    return this.data.currentUser as User;
  }

  get language() {
    return this.data.currentLanguage;
  }

  get emitCaseFn() {
    return this.data.emitCaseFn;
  }

  get updatedCase$() {
    return this.data.updatedCaseObservable;
  }

  get isDataForModalReady$() {
    return this.data.dataReadyObservable;
  }

  get isMobileOrTablet() {
    return this.breakpointService.isMinorThanLg();
  }

  get isHistoryPage() {
    const HISTORY_PAGE = 'history';
    return this.router.url.includes(HISTORY_PAGE);
  }

  get isAssigned() {
    return this.caseData.assigned.id === this.currentUser.id;
  }
}

export interface File {
  file_key: string;
  file_name: string;
  type?: string;
}
